import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HSTSToast } from "../utils/hstsToast";
import { ToastContainer } from "react-toastify";
import { userLogin } from "../services/AuthService";
import { getStudentByEmail } from "../services/StudentService";
import { getCoordinatorByEmail } from "../services/CoordinatorService";
import { useUser } from "../utils/userProvider";

const HSTSLogin = () => {
  const navigator = useNavigate();
  const { refreshUser } = useUser();

  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  const formValidation = () => {
    let valid = true;
    const errorrsCopy = { ...errors };
    if (!username.trim()) {
      errorrsCopy.username = "User name is required.";
      valid = false;
    }
    if (!password.trim()) {
      errorrsCopy.password = "Password is required.";
      valid = false;
    }
    setErrors(errorrsCopy);
    return valid;
  };

  const handleFocus = (field) => {
    if (!errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleBlur = (field, value) => {
    if (!value.trim()) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required.`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const checkUserStatus = async (role, email) => {
    try {
      if (role === "ROLE_STUDENT") {
        const res = await getStudentByEmail(email);
        if (res.data.studentStatus === "enable") {
          navigator("/student");
        } else {
          HSTSToast(
            "error",
            "Your account is IN ACTIVE. Contact to IT Service Desk for help."
          );
        }
      } else if (role === "ROLE_COORDINATOR") {
        const res = await getCoordinatorByEmail(email);
        if (res.data.coordinatorStatus === "enable") {
          navigator("/coordinator");
        } else {
          HSTSToast(
            "error",
            "Your account is IN ACTIVE. Contact IT Service Desk for help."
          );
        }
      }
    } catch (err) {
      console.error("Error fetching user status:", err);
      HSTSToast("error", "Unable to verify status. Contact IT Service Desk.");
    }
  };

  const loginFormHandler = async (e) => {
    e.preventDefault();
    if (!formValidation()) return;

    setIsLoading(true);
    try {
      // console.log("Login: Sending login request...");

      // Send login request
      await userLogin({ username, password });

      // HTTP-only cookie is automatically set by the backend
      // console.log("Login: Login successful, refreshing user context...");

      // Give the cookie a moment to be set
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refresh user data from context (this will call /auth/verify)
      // This returns the user data directly, not just true/false
      const userData = await refreshUser();

      if (!userData || !userData.role) {
        // console.error("Login: Failed to get user data or role");

        HSTSToast("error", "Failed to load user data. Please try again.");
        setIsLoading(false);
        return;
      }

      // console.log("Login: User context refreshed successfully");
      // console.log("Login: User role is:", userData.role);

      // Use the returned userData instead of relying on state
      const role = userData.role;
      const email = userData.email || username;

      // Route based on role
      switch (role) {
        case "ROLE_ADMIN":
          // console.log("Login: Routing to /admin");
          navigator("/admin", { replace: true });
          break;
        case "ROLE_STUDENT":
          // console.log("Login: Checking student status...");
          await checkUserStatus(role, email);
          break;
        case "ROLE_COORDINATOR":
          // console.log("Login: Checking coordinator status...");
          await checkUserStatus(role, email);
          break;
        default:
          // console.warn("Login: Unknown role:", role);
          HSTSToast("warn", "Please try again later");
          break;
      }
    } catch (error) {
      // console.error("Login error:", error);
      HSTSToast("error", "Wrong username or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="wrapper vh-100">
        <div className="row align-items-center h-100">
          <form className="col-lg-3 col-md-6 col-9 mx-auto">
            <div className="card zis-shadow rounded-3">
              <div className="card-body">
                <div className="text-center">
                  <h1
                    className="h6 mb-3 text-black fw-semibold"
                    style={{ fontFamily: "initial", fontSize: "x-large" }}
                  >
                    HETRAS Login
                  </h1>
                </div>
                <div className="form-group">
                  <label className="text-black">Enter username</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.username ? "is-invalid" : ""
                    }`}
                    onChange={(e) => setUserName(e.target.value)}
                    onFocus={() => handleFocus("username")}
                    onBlur={() => handleBlur("username", username)}
                    placeholder="Please enter username"
                    disabled={isLoading}
                  />
                  {errors.username && (
                    <div className="invalid-feedback">{errors.username}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="text-black">Enter password</label>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => handleFocus("password")}
                      onBlur={() => handleBlur("password", password)}
                      placeholder="Please enter password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="input-group-text"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      <i
                        className={`fa ${
                          showPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      />
                    </button>
                  </div>
                  {errors.password && (
                    <div className="invalid-feedback">{errors.password}</div>
                  )}
                </div>

                <div className="form-group mb-3">
                  <button
                    className="btn btn-black w-100 rounded-3"
                    onClick={loginFormHandler}
                    disabled={isLoading}
                  >
                    {isLoading ? "Logging in..." : "Let me in"}
                  </button>
                </div>
                <div className="d-flex form-group">
                  <Link to="/resetPassword" className="ms-auto text-black">
                    Forget password.
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default HSTSLogin;
